import { Avatar, Button, Input, TextField } from '@starter/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Row, RowHeading } from '../account/row.tsx';
import { ConnectedAccounts } from '../components/connected-accounts.tsx';
import { DeleteAccount } from '../components/delete-account.tsx';
import { useAccounts } from '../lib/accounts.ts';
import { authClient, useSession } from '../lib/auth.ts';

/**
 * `/account/profile` — who you are, and the three ways to prove it.
 *
 * The same forms that used to be five cards stacked on one page, in the rows
 * the original arranges them in: the picture, the name, the address, then
 * **Account security** — which is a heading in the product and is what makes a
 * password and a connected provider one subject rather than two boxes.
 *
 * Each row's second line carries the explanation, so no form has to open with
 * a paragraph. Deleting the account is last, and its hint is the one place on
 * this screen where the label is doing safety work rather than labelling.
 */
function useFormState() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  return {
    pending,
    error,
    done,
    start() {
      setPending(true);
      setError(null);
      setDone(null);
    },
    fail(message: string) {
      setPending(false);
      setError(message);
    },
    succeed(message: string) {
      setPending(false);
      setDone(message);
    },
  };
}

function Feedback({
  error,
  done,
  name,
}: {
  error: string | null;
  done: string | null;
  name: string;
}) {
  if (error) {
    return (
      <p className="text-sm text-danger-text" data-testid={`${name}-error`} role="alert">
        {error}
      </p>
    );
  }
  if (done) {
    return (
      <p className="text-sm text-ink-muted" data-testid={`${name}-done`} role="status">
        {done}
      </p>
    );
  }
  return null;
}

/** Two letters at most, which is what a circle this size can hold. */
function initials(name: string): string {
  return name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function NameRow({ name, onSaved }: { name: string; onSaved: () => Promise<unknown> }) {
  const form = useFormState();

  return (
    <Row label="Preferred name" sub="What we call you in the interface and in email.">
      <form
        className="flex flex-col gap-3"
        onSubmit={async (event) => {
          event.preventDefault();
          form.start();
          const value = String(new FormData(event.currentTarget).get('name') ?? '').trim();
          const { error } = await authClient.updateUser({ name: value });
          if (error) {
            form.fail(error.message ?? 'Could not save');
            return;
          }
          await onSaved();
          form.succeed('Saved.');
        }}
      >
        {/* The row is the label, so the control carries none of its own. */}
        <Input
          aria-label="Preferred name"
          data-testid="profile-name"
          defaultValue={name}
          name="name"
          placeholder="Your name"
          required
        />
        <Feedback done={form.done} error={form.error} name="profile" />
        {/* A button is not a banner: it takes its own width inside a 540 column. */}
        <Button
          className="self-start"
          data-testid="profile-submit"
          disabled={form.pending}
          type="submit"
        >
          {form.pending ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </Row>
  );
}

function EmailRow({ email, verified }: { email: string; verified: boolean }) {
  const form = useFormState();

  return (
    <Row
      label="Email"
      sub="Changing it asks the address on file first, never the new one."
      testId="email-section"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink" data-testid="current-email">
          {email}{' '}
          <span className="text-ink-muted" data-testid="email-verified">
            {verified ? '· confirmed' : '· not confirmed'}
          </span>
        </p>

        <form
          className="flex flex-col gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            form.start();
            const newEmail = String(new FormData(event.currentTarget).get('newEmail') ?? '');
            const { error } = await authClient.changeEmail({
              newEmail,
              callbackURL: '/account',
            });
            if (error) {
              form.fail(error.message ?? 'Could not request the change');
              return;
            }
            // The approval goes to the address on file, not the new one — see
            // the comment on sendChangeEmailConfirmation in apps/api/src/auth.ts.
            form.succeed(`Check ${email} and approve the move to ${newEmail}.`);
          }}
        >
          <TextField
            autoComplete="email"
            data-testid="new-email"
            label="New email"
            name="newEmail"
            required
            type="email"
          />
          <Feedback done={form.done} error={form.error} name="email" />
          <Button
            className="self-start"
            data-testid="email-submit"
            disabled={form.pending}
            type="submit"
          >
            {form.pending ? 'Sending…' : 'Change email'}
          </Button>
        </form>
      </div>
    </Row>
  );
}

function PasswordRow({ email, hasPassword }: { email: string; hasPassword: boolean }) {
  const form = useFormState();

  if (!hasPassword) {
    return (
      <Row
        label="Password"
        sub="This account signs in with a provider. Add one and it can do both."
        testId="password-section"
      >
        <div className="flex flex-col gap-3">
          <Feedback done={form.done} error={form.error} name="password" />
          <Button
            className="self-start"
            data-testid="password-set"
            disabled={form.pending}
            onClick={async () => {
              form.start();
              const { error } = await authClient.requestPasswordReset({
                email,
                redirectTo: '/reset-password',
              });
              if (error) {
                form.fail(error.message ?? 'Could not send the link');
                return;
              }
              form.succeed(`We sent a link to ${email}.`);
            }}
            variant="secondary"
          >
            {form.pending ? 'Sending…' : 'Email me a link'}
          </Button>
        </div>
      </Row>
    );
  }

  return (
    <Row
      label="Password"
      sub="Changing it signs out every other session, which is the point of changing it."
      testId="password-section"
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={async (event) => {
          event.preventDefault();
          form.start();
          // Captured before the first await: React clears `currentTarget` once
          // the handler yields, and the reset below runs after the request.
          const element = event.currentTarget;
          const data = new FormData(element);
          const { error } = await authClient.changePassword({
            currentPassword: String(data.get('currentPassword') ?? ''),
            newPassword: String(data.get('newPassword') ?? ''),
            // Changing a password is how someone reacts to a suspicion; leaving
            // the other sessions signed in would answer it with nothing.
            revokeOtherSessions: true,
          });
          if (error) {
            form.fail(error.message ?? 'Could not change the password');
            return;
          }
          element.reset();
          form.succeed('Password changed. Other sessions were signed out.');
        }}
      >
        <TextField
          autoComplete="current-password"
          data-testid="current-password"
          label="Current password"
          name="currentPassword"
          required
          type="password"
        />
        <TextField
          autoComplete="new-password"
          data-testid="new-password"
          description="At least 10 characters."
          label="New password"
          minLength={10}
          name="newPassword"
          required
          type="password"
        />
        <Feedback done={form.done} error={form.error} name="password" />
        <Button
          className="self-start"
          data-testid="password-submit"
          disabled={form.pending}
          type="submit"
        >
          {form.pending ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </Row>
  );
}

function ProfilePage() {
  const { data: session, isPending, refetch } = useSession();
  const accounts = useAccounts();

  if (isPending || !session) return <p className="text-sm text-ink-muted">Loading…</p>;

  const hasPassword = (accounts.data ?? []).some((account) => account.providerId === 'credential');
  const name = session.user.name?.trim() || session.user.email;

  return (
    <div className="flex flex-col" data-testid="profile-section">
      {/*
        Eighty-eight across, which is the original's own number for it. Nothing
        to upload behind it: a signed URL expires and `user.image` would hold a
        dead link within the hour, so the initials stand in until there is a
        route that serves an account's own picture (`prd/DIVERGENCE.md`).
      */}
      <Row label="Picture" sub="Your initials, until a picture can be stored for one.">
        <Avatar
          className="size-[88px] text-[28px]"
          data-testid="profile-avatar"
          fallback={initials(name)}
        />
      </Row>

      <NameRow name={session.user.name} onSaved={refetch} />
      <EmailRow email={session.user.email} verified={session.user.emailVerified} />

      <RowHeading>Account security</RowHeading>
      {accounts.isPending ? null : (
        <>
          <PasswordRow email={session.user.email} hasPassword={hasPassword} />
          <Row label="Sign-in methods" sub="What this account can sign in with.">
            <ConnectedAccounts />
          </Row>
        </>
      )}

      <Row
        label="Delete account"
        sub="This removes the account and everything in it. There is no undo."
      >
        <DeleteAccount />
      </Row>
    </div>
  );
}

export const Route = createFileRoute('/account/profile')({
  component: ProfilePage,
});
